<template>
	<v-dialog
		v-model="dialog"
		max-width="800px"
		:fullscreen="$vuetify.breakpoint.xs ? true : false"
		:transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : ''"
	>
		<template v-slot:activator="{ on, attrs }">
			<v-btn color="secondary" v-bind="attrs" v-on="on">
				<v-icon class="mr-1">mdi-magnify</v-icon>
				受注製品から検索
			</v-btn>
		</template>

		<v-card :tile="!$vuetify.breakpoint.xs">
			<v-app-bar dense dark flat color="primary">
				<v-app-bar-title class="color-white">受注製品から検索</v-app-bar-title>
				<v-spacer />
				<v-btn icon dark @click="dialog = false" color="white">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-app-bar>
			<v-form ref="form" class="pa-2">
				<v-row no-gutters>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							v-model="q.order_number_cont"
							label="受注番号"
							class="mt-4"
							outlined
							dense
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field v-model="q.name_cont" label="案件名" class="mt-4" outlined dense />
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-text-field
							v-model="q.customer_company_name_cont"
							label="向け先名"
							class="mt-4"
							outlined
							dense
						/>
					</v-col>
					<v-col class="p-auto-1" cols="12" sm="6">
						<v-menu
							ref="deadlineRangeMenu"
							v-model="deadlineRangeMenu"
							:close-on-content-click="false"
							:return-value.sync="deadlineRange"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="deadlinesText"
									label="納期"
									class="mt-4"
									clearable
									outlined
									dense
									readonly
									hint="範囲選択が可能です。"
									v-bind="attrs"
									v-on="on"
								></v-text-field>
							</template>
							<v-date-picker
								class="date-picker"
								v-model="deadlineRange"
								no-title
								scrollable
								range
								locale="ja-JP"
							>
								<v-spacer />
								<v-btn text color="grey" @click="deadlineRangeMenu = false"> キャンセル </v-btn>
								<v-btn text color="primary" @click="changeDateRange('deadlineRange', 'deadline')">
									決定
								</v-btn>
							</v-date-picker>
						</v-menu>
					</v-col>
				</v-row>
			</v-form>
			<v-row no-gutters justify="center">
				<v-btn color="secondary" @click="searchMatters(1)">
					<v-icon class="mr-1">mdi-magnify</v-icon>
					検索
				</v-btn>
			</v-row>

			<div class="pa-2" v-if="showTable">
				<v-divider class="mt-4" />
				<template v-if="!selectedMatter">
					<div class="table-container mt-4">
						<v-data-table
							locale="ja-JP"
							:items="matters"
							:headers="matterHeaders"
							:mobile-breakpoint="240"
							hide-default-footer
							disable-filtering
							disable-pagination
							disable-sort
							no-data-text="データがありません。"
						>
							<template v-slot:item="{ item }">
								<tr class="clickable" @click="setSelectedMatter(item)">
									<td>{{ item.name }}</td>
									<td>{{ item.order_number }}</td>
									<td>{{ item.customer_company.name }}</td>
									<td>{{ item.customer_person.name }}</td>
									<td>{{ common.formatDate(item.deadline) }}</td>
									<td>{{ common.formatDate(item.order_date) }}</td>
									<td>{{ item.team_member.user.full_name }}</td>
								</tr>
							</template>
						</v-data-table>
					</div>
					<PaginationModule :pagination="pagination" @get-page="searchMatters($event)" />
				</template>
			</div>

			<div class="pa-2" v-if="selectedMatter">
				<div>
					<v-row no-gutters justify="space-between">
						<h4 class="text-sm-body-1 text-h6 font-weight-bold secondary--text">案件情報</h4>
						<v-btn color="secondary" @click="resetSelectedMatter">
							<v-icon class="mr-1"> mdi-arrow-left </v-icon>
							案件を選択し直す
						</v-btn>
					</v-row>
					<v-row no-gutters>
						<v-col cols="4">
							<h5 class="text-body-1 text-sm-body-2 font-weight-bold">案件名</h5>
						</v-col>
						<v-col cols="8">
							<p class="text-body-1 text-sm-body-2">{{ selectedMatter.name }}</p>
						</v-col>
					</v-row>
					<v-row class="mt-2" no-gutters>
						<v-col cols="4">
							<h5 class="text-body-1 text-sm-body-2 font-weight-bold">受注番号</h5>
						</v-col>
						<v-col cols="8">
							<p class="text-body-1 text-sm-body-2">{{ selectedMatter.order_number }}</p>
						</v-col>
					</v-row>
					<v-row class="mt-2" no-gutters>
						<v-col cols="4">
							<h5 class="text-body-1 text-sm-body-2 font-weight-bold">向け先</h5>
						</v-col>
						<v-col cols="8">
							<p class="text-body-1 text-sm-body-2">{{ selectedMatter.customer_company.name }}</p>
						</v-col>
					</v-row>
					<v-row class="mt-2" no-gutters>
						<v-col cols="4">
							<h5 class="text-body-1 text-sm-body-2 font-weight-bold">向け先担当者</h5>
						</v-col>
						<v-col cols="8">
							<p class="text-body-1 text-sm-body-2">{{ selectedMatter.customer_person.name }}</p>
						</v-col>
					</v-row>
					<v-row class="mt-2" no-gutters>
						<v-col cols="4">
							<h5 class="text-body-1 text-sm-body-2 font-weight-bold">納期</h5>
						</v-col>
						<v-col cols="8">
							<p class="text-body-1 text-sm-body-2">
								{{ common.formatDate(selectedMatter.deadline) }}
							</p>
						</v-col>
					</v-row>
					<v-row class="mt-2" no-gutters>
						<v-col cols="4">
							<h5 class="text-body-1 text-sm-body-2 font-weight-bold">受注日</h5>
						</v-col>
						<v-col cols="8">
							<p class="text-body-1 text-sm-body-2">
								{{ common.formatDate(selectedMatter.order_date) }}
							</p>
						</v-col>
					</v-row>
					<v-row class="mt-2" no-gutters>
						<v-col cols="4">
							<h5 class="text-body-1 text-sm-body-2 font-weight-bold">担当者</h5>
						</v-col>
						<v-col cols="8">
							<p class="text-body-1 text-sm-body-2">
								{{ selectedMatter.team_member.user.full_name }}
							</p>
						</v-col>
					</v-row>
				</div>
				<div class="table-container mt-4">
					<v-data-table
						locale="ja-JP"
						v-model="orderIndividualInfos"
						item-key="id"
						show-select
						:items="selectedMatter.order_individual_infos"
						:headers="infoHeaders"
						:mobile-breakpoint="240"
						hide-default-footer
						disable-filtering
						disable-pagination
						disable-sort
						no-data-text="データがありません。"
					/>
				</div>
			</div>

			<v-card-actions>
				<v-row class="mt-4" no-gutters>
					<v-col cols="0" sm="2"></v-col>
					<v-col cols="12" sm="8">
						<v-row v-if="selectedMatter" no-gutters justify="center">
							<v-btn
								class="ma-1"
								:disabled="!orderIndividualInfos.length"
								@click="setOrderIndividualInfos"
								color="secondary"
							>
								<v-icon class="mr-1">mdi-check-circle-outline</v-icon>
								決定
							</v-btn>
						</v-row>
					</v-col>
					<v-col cols="0" sm="2">
						<v-row v-if="!$vuetify.breakpoint.xs" no-gutters justify="end">
							<v-btn color="grey darken-1" text @click="dialog = false"> 閉じる </v-btn>
						</v-row>
					</v-col>
				</v-row>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import axios from "axios"
import Qs from "qs"
import authenticate from "../../../helper/authenticate"
import errorHandling from "../../../helper/errorHandling"
import common from "../../../helper/common"
import IncrementalSearchForm from "../../shared/IncrementalSearchForm.vue"
import PaginationModule from "../../../modules/PaginationModule.vue"

export default {
	props: {},
	components: {
		IncrementalSearchForm,
		PaginationModule,
	},
	data() {
		return {
			dialog: false,
			deadlineRange: [],
			deadlineRangeMenu: false,
			matters: [],
			orderIndividualInfos: [],
			selectedMatter: null,
			matterHeaders: [
				{
					text: "案件名",
					value: "name",
					width: "200px",
				},
				{
					text: "受注番号",
					value: "order_number",
					width: "200px",
				},
				{
					text: "向け先",
					value: "customer_company.name",
					width: "200px",
				},
				{
					text: "向け先担当者",
					value: "customer_person.name",
					width: "200px",
				},
				{
					text: "納期",
					value: "deadline",
					width: "150px",
				},
				{
					text: "受注日",
					value: "order_date",
					width: "150px",
				},
				{
					text: "担当者",
					value: "team_member.user.full_name",
					width: "200px",
				},
			],
			infoHeaders: [
				{
					text: "製造番号",
					value: "ordered_product.product_number",
					width: "120px",
				},
				{
					text: "枝番号",
					value: "sub_number",
					width: "80px",
				},
				{
					text: "製品名",
					value: "ordered_product.product.name",
					width: "200px",
				},
				{
					text: "サブネーム",
					value: "ordered_product.sub_name",
					width: "120px",
				},
				{
					text: "個数",
					value: "quantity",
					width: "80px",
				},
				{
					text: "状況",
					value: "status_i18n",
					width: "100px",
				},
				{ text: "集荷予定日", value: "delivery_info.planned_shipping", width: "200px" },
				{ text: "納期", value: "delivery_info.formatted_deadline", width: "150px" },
				{ text: "日程確認", value: "delivery_info.dateConfirm", width: "80px" },
				{ text: "郵便番号", value: "delivery_info.zipcode", width: "120px" },
				{ text: "住所", value: "delivery_info.full_address", width: "250px" },
				{ text: "電話番号", value: "delivery_info.phone", width: "150px" },
				{ text: "FAX", value: "delivery_info.fax", width: "150px" },
				{ text: "備考", value: "delivery_info.memo", width: "200px" },
			],
			q: {
				order_number_cont: null,
				name_cont: null,
				customer_company_name_cont: null,
				deadline_lteq: null,
				deadline_gteq: null,
			},
			pagination: {
				count: null,
				current: 1,
				limit_value: null,
				next: null,
				pages: null,
				previous: null,
			},
			showTable: false,
			selectedMatter: null,
			common: common,
		}
	},
	methods: {
		setSelectedMatter(matter) {
			const order_individual_infos = matter.ordered_products
				.map(
					(op) =>
						op.order_individual_infos
							.filter((info) => info.status != "deleted") // 削除ずみは除外
							.map((info) => {
								info.ordered_product = op
								info.delivery_info = {
									...info.delivery_info,
									planned_shipping: common.formatDateWithNullableTime(
										info.delivery_info,
										"planned_shipping_date",
										"planned_shipping_time"
									),
									formatted_deadline: common.formatDate(info.delivery_info.deadline),
									dateConfirm: info.delivery_info.date_confirm ? "◯" : "×",
									full_address: `${info.delivery_info.prefecture}${info.delivery_info.city}${info.delivery_info.address}`,
								}
								return info
							}) // order_productを追加して配列に
				)
				.flat() // 二重の配列を一重に
			this.selectedMatter = {
				...matter,
				order_individual_infos: order_individual_infos,
			}
			this.orderIndividualInfos = order_individual_infos
		},

		searchMatters(page) {
			const headers = authenticate.setHeader()
			const params = { page: page, q: this.q }
			const paramsSerializer = (params) => Qs.stringify(params, { arrayFormat: "brackets" })

			axios
				.get("/erp/v1/delivery_orders/search_matters", {
					params,
					paramsSerializer,
					headers,
				})
				.then((response) => {
					this.showTable = true
					this.matters = response.data.matters
					this.pagination = response.data.pagination
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		setOrderIndividualInfos() {
			this.$emit("set-infos", this.orderIndividualInfos)
			this.dialog = false
		},
		resetSelectedMatter() {
			this.selectedMatter = null
			this.orderIndividualInfos = []
		},
		changeDateRange(dateRange, queryName) {
			if (this[dateRange].length) {
				const firstDate = this[dateRange][0]
				const lastDate = this[dateRange][this[dateRange].length - 1]
				if (firstDate > lastDate) {
					this.q[`${queryName}_gteq`] = lastDate
					this.q[`${queryName}_lteq`] = firstDate
				} else {
					this.q[`${queryName}_gteq`] = firstDate
					this.q[`${queryName}_lteq`] = lastDate
				}
			} else {
				this.q[`${queryName}_gteq`] = null
				this.q[`${queryName}_lteq`] = null
			}
			this.$refs[`${dateRange}Menu`].save(this[dateRange])
		},
		selectData() {
			this.$emit("set-infos", this.orderIndividualInfos)
			this.dialog = false
		},
	},
	computed: {
		deadlinesText: {
			get() {
				return this.deadlineRange.length
					? this.deadlineRange.map((d) => common.formatDate(d)).join(" ~ ")
					: null
			},
			set(newVal) {
				this.deadlineRange = []
				this.changeDateRange("deadlineRange", "deadline")
				return
			},
		},
	},
}
</script>